<script lang="ts">
  import { router } from "@inertiajs/svelte"
  import { Filter, Trash2 } from "@lucide/svelte"
  import { onMount } from "svelte"

  import TaskInstanceFilterDialog from "@/components/task-instance-filter-dialog.svelte"
  import { Button } from "@/components/ui/button"
  import { Card, CardContent } from "@/components/ui/card"
  import ConfirmationDialog from "@/components/ui/confirmation-dialog.svelte"
  import AppLayout from "@/layouts/app-layout.svelte"
  import { adminIndexTaskInstancesPath, taskInstancePath } from "@/routes"
  import type { BreadcrumbItem, TaskInstance, User } from "@/types"

  interface Props {
    taskInstances: TaskInstance[]
    pagination: {
      page: number
      perPage: number
      total: number
      hasMore: boolean
    }
    filters: {
      completed?: string
      approved?: string
      paid?: string
      expired?: string
      userId?: number
    }
    users: User[]
  }

  let {
    taskInstances: initialTaskInstances = [],
    pagination: initialPagination = {
      page: 1,
      perPage: 20,
      total: 0,
      hasMore: false,
    },
    filters: initialFilters = {},
    users = [],
  }: Props = $props()

  let taskInstances = $state<TaskInstance[]>(initialTaskInstances)
  let pagination = $state(initialPagination)
  let isLoading = $state(false)
  let deleteDialogOpen = $state(false)
  let pendingDeleteTaskId = $state<number | null>(null)
  let filterDialogOpen = $state(false)

  let filters = $state({
    completed: (initialFilters.completed
      ? [initialFilters.completed]
      : []) as Array<"true" | "false">,
    approved: (initialFilters.approved
      ? [initialFilters.approved]
      : []) as Array<"true" | "false">,
    paid: (initialFilters.paid ? [initialFilters.paid] : []) as Array<
      "true" | "false"
    >,
    expired: (initialFilters.expired ? [initialFilters.expired] : []) as Array<
      "true" | "false"
    >,
    userId: initialFilters.userId ? [initialFilters.userId] : ([] as number[]),
  })

  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: "Task Instances",
      href: adminIndexTaskInstancesPath(),
    },
  ]

  function formatAssignedOn(task: TaskInstance): string {
    if (!task.assigned_to || !task.created_at) return ""
    const date = new Date(task.created_at)
    const dateStr = date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    })
    const timeStr = date
      .toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
      })
      .toLowerCase()
    return `Assigned to ${task.assigned_to.name} on ${dateStr} at ${timeStr} (incomplete)`
  }

  function formatCompletedBy(task: TaskInstance): string {
    if (!task.assigned_to || !task.completed_at) return ""
    const date = new Date(task.completed_at)
    const dateStr = date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    })
    const timeStr = date
      .toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
      })
      .toLowerCase()
    let result = `Completed by ${task.assigned_to.name} on ${dateStr} at ${timeStr}`
    if (task.paid) {
      result = `${result} (paid)`
    }
    return result
  }

  function loadMore() {
    if (isLoading || !pagination.hasMore) return

    isLoading = true
    const nextPage = pagination.page + 1

    const params = new URLSearchParams()
    filters.completed.forEach((value) => params.append("completed[]", value))
    filters.approved.forEach((value) => params.append("approved[]", value))
    filters.paid.forEach((value) => params.append("paid[]", value))
    filters.expired.forEach((value) => params.append("expired[]", value))
    filters.userId.forEach((id) => params.append("user_id[]", id.toString()))
    params.set("page", nextPage.toString())

    router.get(
      `${adminIndexTaskInstancesPath()}?${params.toString()}`,
      {},
      {
        preserveState: true,
        preserveScroll: true,
        only: ["taskInstances", "pagination"],
        onSuccess: (page) => {
          const newInstances = page.props.taskInstances as TaskInstance[]
          taskInstances = [...taskInstances, ...newInstances]
          pagination = page.props.pagination as typeof pagination
          isLoading = false
        },
        onError: () => {
          isLoading = false
        },
      },
    )
  }

  function applyFilters() {
    const wasDialogOpen = filterDialogOpen
    const params = new URLSearchParams()
    filters.completed.forEach((value) => params.append("completed[]", value))
    filters.approved.forEach((value) => params.append("approved[]", value))
    filters.paid.forEach((value) => params.append("paid[]", value))
    filters.expired.forEach((value) => params.append("expired[]", value))
    filters.userId.forEach((id) => params.append("user_id[]", id.toString()))

    router.get(
      `${adminIndexTaskInstancesPath()}${params.toString() ? `?${params.toString()}` : ""}`,
      {},
      {
        preserveState: true,
        preserveScroll: true,
        only: ["taskInstances", "pagination", "filters"],
        onSuccess: () => {
          if (wasDialogOpen) {
            filterDialogOpen = true
          }
        },
      },
    )
  }

  function handleDeleteClick(taskId: number) {
    pendingDeleteTaskId = taskId
    deleteDialogOpen = true
  }

  function confirmDelete() {
    if (pendingDeleteTaskId !== null) {
      const redirectPath = adminIndexTaskInstancesPath()
      router.delete(
        `${taskInstancePath(pendingDeleteTaskId)}?redirect_path=${encodeURIComponent(redirectPath)}`,
        {
          preserveScroll: true,
          onSuccess: () => {
            taskInstances = taskInstances.filter(
              (t) => t.id !== pendingDeleteTaskId,
            )
            pagination = {
              ...pagination,
              total: pagination.total - 1,
            }
            pendingDeleteTaskId = null
            deleteDialogOpen = false
          },
        },
      )
    }
  }

  let observer: IntersectionObserver | null = null
  let loadMoreElement: HTMLElement | null = $state(null)

  $effect(() => {
    taskInstances = initialTaskInstances
    pagination = initialPagination
    const completed = initialFilters.completed
      ? [initialFilters.completed as "true" | "false"]
      : []
    const approved = initialFilters.approved
      ? [initialFilters.approved as "true" | "false"]
      : []
    const paid = initialFilters.paid
      ? [initialFilters.paid as "true" | "false"]
      : []
    const expired = initialFilters.expired
      ? [initialFilters.expired as "true" | "false"]
      : []
    const userId = initialFilters.userId ? [initialFilters.userId] : []
    const newFilters = { completed, approved, paid, expired, userId }
    if (
      JSON.stringify(newFilters.completed) !==
        JSON.stringify(filters.completed) ||
      JSON.stringify(newFilters.approved) !==
        JSON.stringify(filters.approved) ||
      JSON.stringify(newFilters.paid) !== JSON.stringify(filters.paid) ||
      JSON.stringify(newFilters.expired) !== JSON.stringify(filters.expired) ||
      JSON.stringify(newFilters.userId) !== JSON.stringify(filters.userId)
    ) {
      filters = newFilters
    }
  })

  onMount(() => {
    if (loadMoreElement) {
      observer = new IntersectionObserver(
        (entries) => {
          if (entries[0]?.isIntersecting && pagination.hasMore && !isLoading) {
            loadMore()
          }
        },
        { threshold: 0.1 },
      )
      observer.observe(loadMoreElement)
    }

    return () => {
      if (observer && loadMoreElement) {
        observer.unobserve(loadMoreElement)
      }
    }
  })
</script>

<svelte:head>
  <title>Task Instances - Chorizo</title>
</svelte:head>

<AppLayout {breadcrumbs}>
  <div class="flex h-full flex-1 flex-col gap-6 overflow-x-auto rounded-xl p-4">
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-3xl font-bold">Task Instances</h1>
        <p class="text-muted-foreground mt-2">Manage all task instances</p>
      </div>
      <Button variant="outline" onclick={() => (filterDialogOpen = true)}>
        <Filter class="h-4 w-4" />
        Filter
      </Button>
    </div>

    <div class="space-y-2 md:max-w-[calc(50%-0.5rem)]">
      {#if taskInstances.length === 0}
        <Card>
          <CardContent class="flex flex-col items-center justify-center p-12">
            <p class="text-muted-foreground">No task instances found</p>
          </CardContent>
        </Card>
      {:else}
        {#each taskInstances as task (task.id)}
          {@const isIncomplete = !task.completed_at}
          <div
            class="hover:bg-muted/50 flex flex-col gap-2 rounded px-2 py-1.5 xl:flex-row xl:items-center xl:justify-between"
          >
            <div
              class="flex flex-1 items-center justify-between xl:justify-start xl:gap-3"
            >
              <div class="font-medium">
                {task.task_template?.name || "Task"}
              </div>
              {#if task.task_template?.wage}
                <span class="text-sm font-medium xl:hidden"
                  >${Number.parseFloat(task.task_template.wage).toFixed(
                    2,
                  )}</span
                >
              {/if}
            </div>
            <div class="flex items-center gap-3">
              {#if task.task_template?.wage}
                <span class="hidden text-sm font-medium xl:inline"
                  >${Number.parseFloat(task.task_template.wage).toFixed(
                    2,
                  )}</span
                >
              {/if}
              <Button
                size="sm"
                onclick={() => handleDeleteClick(task.id)}
                variant="destructive"
                class="h-7 px-2 text-xs"
              >
                <Trash2 class="h-4 w-4" />
                {isIncomplete ? "Cancel" : "Delete"}
              </Button>
            </div>
          </div>
          {#if !task.completed_at}
            <div class="text-muted-foreground -mt-1 mb-1 px-2 text-sm">
              {formatAssignedOn(task)}
            </div>
          {:else if task.assigned_to && task.completed_at}
            <div class="text-muted-foreground -mt-1 mb-1 px-2 text-sm">
              {formatCompletedBy(task)}
            </div>
          {/if}
        {/each}

        {#if pagination.hasMore}
          <div
            bind:this={loadMoreElement}
            class="flex items-center justify-center py-4"
          >
            {#if isLoading}
              <p class="text-muted-foreground text-sm">Loading...</p>
            {:else}
              <p class="text-muted-foreground text-sm">
                Showing {taskInstances.length} of {pagination.total}
              </p>
            {/if}
          </div>
        {:else if taskInstances.length > 0}
          <div class="flex items-center justify-center py-4">
            <p class="text-muted-foreground text-sm">
              Showing all {pagination.total} task instances
            </p>
          </div>
        {/if}
      {/if}
    </div>
  </div>

  {#if pendingDeleteTaskId !== null}
    {@const taskToDelete = taskInstances.find(
      (t) => t.id === pendingDeleteTaskId,
    )}
    {@const isIncomplete = taskToDelete && !taskToDelete.completed_at}
    <ConfirmationDialog
      bind:open={deleteDialogOpen}
      title={isIncomplete ? "Cancel Task Instance" : "Delete Task Instance"}
      description={isIncomplete
        ? "Are you sure you want to cancel this task instance? This action cannot be undone."
        : "Are you sure you want to delete this task instance? This action cannot be undone."}
      variant="destructive"
      confirmLabel={isIncomplete ? "Cancel" : "Delete"}
      onConfirm={confirmDelete}
    />
  {/if}

  <TaskInstanceFilterDialog
    bind:open={filterDialogOpen}
    completedFilters={filters.completed}
    approvedFilters={filters.approved}
    paidFilters={filters.paid}
    expiredFilters={filters.expired}
    userFilters={filters.userId}
    {users}
    onCompletedChange={(value) => {
      filters.completed = value
      applyFilters()
    }}
    onApprovedChange={(value) => {
      filters.approved = value
      applyFilters()
    }}
    onPaidChange={(value) => {
      filters.paid = value
      applyFilters()
    }}
    onExpiredChange={(value) => {
      filters.expired = value
      applyFilters()
    }}
    onUserChange={(value) => {
      filters.userId = value
      applyFilters()
    }}
    onClose={() => {
      filterDialogOpen = false
    }}
  />
</AppLayout>
